'use strict';
/* ==========================================================================
   levels/video.js — step 1, THE BIG DROP: hosts the interactive player
   (shared engine core/player.js) and registers one scene per script segment (s1–s7).

   Every scene is a pure function of the part time t. Visual beats are keyed
   to the dialogue with ctx.L / ctx.W (through VK.cues, which keeps cues from
   other parts finite), so the choreography follows whatever the VO timing is.
   Every moving cart is a Tracks sim; every number on screen comes from
   sim.sample(...) (or the sim's own m / g / total0).

   Staging (levels/video_kit.js): a chapter card tears away at the start of
   each segment and the next one slides in at its end; puppets stand full
   size for dialogue and shrink to bottom-corner insets for action, with
   their bubbles kept in a safe zone away from the cart / formula / chart.
   ========================================================================== */
(function () {
  const S = Player.scenes, C = VK.CARD, K = VK.K, vw = VK.vw;
  const dur = id => { const w = Audio.words && Audio.words[id]; return w && w.dur > 0 ? w.dur : Math.max(0.6, 0.065 * String(Audio.lines[id] || '').length); };
  const CHART_L = [30, 30, 380, 380];              // chart top-left (s3, s6)
  const chartAt = (box, st, o) => Coaster.drawChart(box[0], box[1], box[2], box[3], st, o);
  const lastLine = part => part.lines[part.lines.length - 1];
  const OUT = 'rgba(37,60,90,0.9)';

  /* the cart's pose inside the world (px) — same maths as Coaster.drawCart */
  function cartPose(tr, v, s, h) {
    const p = tr.at(s), railW = Math.max(6, 0.55 * v.k), lift = h / 2 + railW / 2 - 2;
    const nx = -Math.sin(p.angle), ny = -Math.cos(p.angle), d = IMG.cart ? Coaster.CART_FIT.dy * h : 0;   // wheels on the rail
    return { x: v.X(p.x) + nx * (lift - d), y: v.Y(p.y) + ny * (lift - d), rot: -p.angle, p };
  }
  /* hand-placed riders (per image): rh = rider height in cart heights, feet = how far below the cart's centre the
     feet sit (in rider heights; the cart body hides everything under the seat line), dx = along the cart (cart
     heights, + = forward), flip = mirror so the rider faces the direction of travel (right) */
  const RIDERS = {
    max_cross: { rh: 3.4, feet: 0.36, dx: -0.2, flip: true },
    max_point: { rh: 3.4, feet: 0.36, dx: -0.2, flip: false },
    elephant: { rh: 2.25, feet: 0.30, dx: 0.02, flip: true },
  };
  VK.RIDERS = RIDERS;
  /* cart + a rider we draw ourselves (squash, flip, eraser clip, hop-in).
     o: {h, rider, hop: 0..1 (0 = in the air above the seat, 1 = seated), sx, sy, erase, jx, jy, glow, seed} */
  function cartRider(tr, v, s, o = {}) {
    const h = o.h ?? 50, pose = cartPose(tr, v, s, h), R_ = RIDERS[o.rider] || { rh: 2.4, feet: 0.3, dx: 0, flip: false };
    const hop = o.hop ?? 1;
    if (o.rider && hop > 0 && (o.erase ?? 0) < 1) {
      const im = IMG[o.rider], rh = h * R_.rh;
      // hop in: a little arc from behind the cart, landing with a squash (pure in hop)
      const air = 1 - E.outQuad(clamp(hop / 0.8)), land = hop > 0.8 ? Math.sin(P(hop, 0.8, 1) * Math.PI) : 0;
      const hx = -air * h * 1.6, hy = -Math.sin(air * Math.PI * 0.5) * rh * 0.75;
      g.save(); g.translate(pose.x + (o.jx || 0), pose.y + (o.jy || 0)); g.rotate(pose.rot);
      // seated: clip the rider at the seat line so the cart hides the legs (no clip while in the air)
      const rw = rh * 2, seat = air > 0.02 ? rh * 3 : h * 0.02;
      g.beginPath(); g.rect(-rw / 2 + rw * (o.erase || 0), -rh * 3, rw, rh * 3 + seat); g.clip();
      const sx = (o.sx ?? 1) * (1 + 0.12 * land), sy = (o.sy ?? 1) * (1 - 0.14 * land);
      const fy = rh * R_.feet * (1 - air) + hy;
      if (im) cut(o.rider, R_.dx * h + hx, fy, rh, { ax: 0.5, ay: 1, flip: R_.flip, sx, sy, depth: 0.8, seed: 55 });
      else { paperRect(hx, fy - rh * 0.35, rh * 0.42, rh * 0.7, PAL.navy, { seed: 56, depth: 0.8 }); paperCircle(hx, fy - rh * 0.8, rh * 0.16, PAL.pink, { seed: 57 }); }
      g.restore();
    }
    g.save(); g.translate(o.jx || 0, o.jy || 0);
    Coaster.drawCart(tr, v, s, { h, glow: o.glow || 0, seed: o.seed ?? 5 });
    g.restore();
    return pose;
  }

  /* ======================================================================
     s1 — cold open: the Big Drop, the cart parked on top, the title
     ====================================================================== */
  S.s1 = {
    get timing() { return { lead: { lines: C.lead }, hold: { lines: 1.1 }, gaps: { s1_02: 0.8 } }; },
    sfx(part, ctx) {
      const c = VK.cues(ctx);
      return [...VK.chapterSfx(part),
        { name: 'wind', t: C.hold, loop: true, dur: 6, gain: 0.12 },
        { name: 'pop', t: c.W('s1_01', 'Big'), gain: 0.4 }, { name: 'pop', t: c.W('s1_01', 'Drop'), gain: 0.35 },
        { name: 'scribble', t: c.W('s1_01', 'engine') + 0.35, gain: 0.35 }, { name: 'scribble', t: c.W('s1_01', 'motor') + 0.3, gain: 0.35 },
        { name: 'scribble', t: c.W('s1_01', 'Once'), gain: 0.3 },
        { name: 'paper', t: c.L('s1_02') - 0.5, gain: 0.4 },
        { name: 'ding', t: c.W('s1_03', 'Energy'), gain: 0.4 },
        { name: 'whoosh', t: c.W('s1_05', 'seventy'), gain: 0.45 }];
    },
    draw(t, part, ctx) {
      const c = VK.cues(ctx), R = Tracks.get('s3'), tr = R.track;
      const t1 = c.L('s1_01'), tTw = c.W('s1_01', 'Twenty'), tEng = c.W('s1_01', 'engine'), tMot = c.W('s1_01', 'motor');
      const tOnce = c.W('s1_01', 'Once'), tNoth = c.W('s1_01', 'nothing');
      const t2 = c.L('s1_02'), tEn = c.W('s1_03', 'Energy'), t4 = c.L('s1_04'), tSev = c.W('s1_05', 'seventy');
      const camAt = u => VK.cam([
        VK.frame(48, 915, 0.84),                        // establishing: the whole ride
        VK.frame(28, 1000, 1.4, tTw - 0.8, 2.2),        // push in on the 20 m hill
        VK.frame(42, 1000, 1.2, tOnce - 0.3, 1.6),      // follow the line down the drop
        VK.frame(42, 965, 1.0, t2 - 0.4, 1.1),          // two-shot: the ride between them
        VK.frame(47, 985, 1.2, tSev - 0.9, 1.3),        // toward the bottom of the drop
      ], u);
      const cam = camAt(t);
      // the drop the chalk arrow runs down (the bubble keeps off it)
      const dropRects = u => { const cm = camAt(u), out = []; for (let x = 33; x <= 56; x += 3) { const p = tr.at(tr.sAtX(x)), [px, py] = VK.toScreen(cm, p.x, p.y); out.push([px - 60, py - 110, px + 60, py + 10]); } return out; };
      const parked = Tracks.get('s2').sim.sample(1e9);   // the s2 chain lift leaves it parked on top
      const vBottom = R.sim.sample(R.marks.tBottom).speed;
      VK.world(cam, () => {
        VK.ground(cam);
        Coaster.drawTrack(tr, vw, { seed: 3, prog: E.outCubic(P(t, 0.1, 1.4)) });
        Coaster.drawRuler(vw, 5, { y1: 20, seed: 4, side: 'left' });
        Coaster.drawCart(tr, vw, parked.s, { h: 54 });
        // "once it leaves the top, nothing pushes it": a chalk arrow down the drop
        const pts = []; for (let x = 33; x <= 54; x += 1) { const p = tr.at(tr.sAtX(x)); pts.push([vw.X(p.x) - Math.sin(p.angle) * 44, vw.Y(p.y) - Math.cos(p.angle) * 44]); }
        const fade = unpop(t, t2 - 0.4, 0.4);
        ink(pts, { w: 7, color: PAL.cream, seed: 12, prog: P(t, tOnce, tNoth), step: 12, alpha: fade });
        if (t > tNoth - 0.3) { const e = pts[pts.length - 1]; ink([[e[0] - 30, e[1] - 22], e, [e[0] - 30, e[1] + 20]], { w: 7, color: PAL.cream, seed: 13, prog: P(t, tNoth - 0.3, tNoth), step: 6, alpha: fade }); }
      });
      // screen-space bits that ride with the cart: no ENGINE, no MOTOR
      const [cx, cy] = VK.toScreen(cam, parked.x, parked.y);
      [['ENGINE', tEng, -250, -30, 1], ['MOTOR', tMot, -210, -130, 2]].forEach(([w, t0, dx, dy, k]) => {
        const out = unpop(t, t2 - 0.5), s = pop(t, t0, 0.3) * out;
        if (s <= 0) return;
        VK.tag(w, cx + dx, cy + dy, 40, s, { rot: -0.08 * k + 0.06, seed: 70 + k, color: PAL.white });
        g.save(); g.globalAlpha *= out;
        VK.strike(cx + dx - 90, cy + dy + 22, cx + dx + 100, cy + dy - 22, P(t, t0 + 0.35, t0 + 0.6), 80 + k);
        g.restore();
      });
      // title on "Big Drop"; ENERGY on "Energy"
      ransom('THE BIG DROP', 960, 132, 112, c.W('s1_01', 'Big'), { seed: 11, exitT: t2 - 0.3 });
      ransom('ENERGY', 960, 140, 104, tEn, { seed: 14, exitT: t4 - 0.2 });
      if (t < t4) hand('…and where it hides', 1000, 238, 52, c.W('s1_03', 'where'), { color: PAL.cream, outline: OUT, seed: 15, rot: -0.02 });
      // "a lecture that does seventy km/h": the top speed, from the s3 sim
      const [bx, by] = VK.toScreen(cam, 56, 0);
      VK.tag('top speed ' + Math.round(Coaster.kmh(vBottom)) + ' km/h', bx + 60, by - 120, 44, pop(t, tSev, 0.35), { color: PAL.red, ink: PAL.cream, seed: 90, rot: -0.06 });
      if (t > tSev) speedLines(bx - 40, by - 40, 1, 0, 90, 60, 4, 91 + (BF % 3), 0.7 * P(t, tSev, tSev + 0.3));
      // cast: Voss narrates the cold open from the corner, Max joins for the two-shot
      const st = VK.stage(t, part, [[0, 'off', 'enterR'], [t1 - 0.5, 'off', 'inset', 0.8], [t2 - 0.55, 'two', 'two', 0.8]]);
      VK.cast(t, ctx, st, { voss: t > tSev - 0.2 ? 'point' : null }, { avoid: u => (u < t2 - 0.4 ? dropRects(u) : []), key: 's1', top: 300 });
      VK.chapter(t, part);
    },
  };

  /* ======================================================================
     s2 — the chain lift (real 2 m/s), PE = m g h, numbers into the slots
     ====================================================================== */
  const S2_SIM0 = 3.5;                    // the lift is already running under the title card
  const s2t = (t, part) => (part.kind === 'lines' ? S2_SIM0 + t : 1e9);
  const F2 = { y: 120, size: 96, PE: 540, eq: 680, m: 810, g: 940, h: 1070, ny: 312 };
  function s2Formula(c, R, top) {
    const tP = c.W('s2_03', 'P');
    VK.tokens([{ s: 'PE', x: F2.PE, t0: tP }, { s: '=', x: F2.eq, t0: c.W('s2_03', 'equals') },
      { s: 'm', x: F2.m, t0: c.W('s2_03', 'm') }, { s: 'g', x: F2.g, t0: c.W('s2_03', 'g') }, { s: 'h', x: F2.h, t0: c.W('s2_03', 'h') }], F2.y, F2.size, 21);
    VK.label('mass', F2.m, 222, 40, c.W('s2_03', 'Mass'), { seed: 24 });
    VK.label('gravity', F2.g, 222, 40, c.W('s2_03', 'gravity'), { seed: 25 });
    VK.label('height', F2.h, 222, 40, c.W('s2_03', 'height', 2), { seed: 26 });
    // the numbers line: slots, then numbers that drop in as she says them (all from the sim)
    const t4 = c.L('s2_04'), ny = F2.ny;
    [F2.m, F2.g, F2.h].forEach(x => VK.slot(x, ny, 104, 74, P(T, t4, t4 + 0.3)));
    VK.chip(String(R.sim.m), F2.m, ny, 46, c.W('s2_04', 'five') + 0.2, { seed: 31, color: PAL.mustard });
    VK.label('×', (F2.m + F2.g) / 2, ny, 50, c.W('s2_04', 'gravity'), { dur: 0.01, seed: 32 });
    VK.chip(String(Math.round(R.sim.g)), F2.g, ny, 46, c.W('s2_04', 'ten'), { seed: 33, color: PAL.mustard });
    VK.label('×', (F2.g + F2.h) / 2, ny, 50, c.W('s2_04', 'twenty') - 0.1, { dur: 0.01, seed: 34 });
    VK.chip(String(Math.round(top.y)), F2.h, ny, 46, c.W('s2_04', 'twenty'), { seed: 35, color: PAL.mustard });
    VK.label('=', F2.h + 92, ny, 58, c.W('s2_04', 'thousand') - 0.15, { dur: 0.01, seed: 36 });
    VK.chip(Coaster.fmtJ(top.PE), F2.h + 262, ny, 48, c.W('s2_04', 'thousand'), { seed: 37, color: PAL.cream, ink: PAL.red });
  }
  S.s2 = {
    get timing() {
      const R = Tracks.get('s2'), arrive = R.marks.tTop - S2_SIM0, e1 = C.lead + dur('s2_01');
      return { lead: { lines: C.lead }, gaps: { s2_02: Math.max(0.6, arrive + 0.45 - e1), s2_05: 0.7, s2_06: 0.45 }, hold: { lines: 1.3, reply: 1.1 } };
    },
    sfx(part, ctx) {
      const c = VK.cues(ctx), R = Tracks.get('s2'), out = VK.chapterSfx(part);
      if (part.kind === 'lines') {
        out.push({ name: 'chain_lift', t: 0.2, loop: true, dur: R.marks.tTop - S2_SIM0 - 0.2, gain: 0.45 });
        out.push({ name: 'thunk', t: R.marks.tTop - S2_SIM0, gain: 0.45 });
        [['s2_03', 'P'], ['s2_03', 'equals'], ['s2_03', 'm'], ['s2_03', 'g'], ['s2_03', 'h']].forEach(([id, w]) => out.push({ name: 'pop', t: c.W(id, w), gain: 0.35 }));
        [['Mass', 1], ['gravity', 1], ['height', 2]].forEach(([w, n]) => out.push({ name: 'scribble', t: c.W('s2_03', w, n), gain: 0.25 }));
        [['five', 0.5], ['ten', 0.3], ['twenty', 0.3]].forEach(([w, d]) => out.push({ name: 'thunk', t: c.W('s2_04', w) + d, gain: 0.4 }));
        out.push({ name: 'ding', t: c.W('s2_04', 'thousand') + 0.35, gain: 0.45 });
        out.push({ name: 'strain', t: c.L('s2_06'), gain: 0.35 });
      } else out.push({ name: 'scribble', t: 0.6, gain: 0.3 });
      return out;
    },
    draw(t, part, ctx) {
      const c = VK.cues(ctx), R = Tracks.get('s2'), tr = R.track;
      const st = R.sim.sample(s2t(t, part)), top = R.sim.sample(1e9);
      if (ctx.asking) return s2Ask(t, part, ctx, c, R, top);
      if (part.kind === 'reply') return s2Reply(t, part, ctx, c, R, top);
      const tArr = R.marks.tTop - S2_SIM0, t3 = c.L('s2_03'), t5 = c.L('s2_05'), t6 = c.L('s2_06'), e6 = c.E('s2_06');
      const followX = VK.smooth(u => R.sim.sample(s2t(u, part)).x, t, 0.8) + 6;
      const cam = VK.cam([
        VK.frame(Math.min(followX, 36), 1010, 1.5),
        VK.frame(37, 985, 1.2, t3 - 0.6, 1.4),                                  // room for the formula
        { t: t5 + 0.9, d: 1.3, x: 30.6, y: 20.6, z: 3.0, sx: 900, sy: 640 },     // close-up: very intense sitting
        VK.frame(37, 985, 1.2, e6 + 0.35, 0.8),
      ], t);
      const sit = VK.win(t, t6 - 0.2, e6 + 0.2, 0.25);
      const j = sit > 0 ? [jit(401, 1.3 * sit), jit(402, 1.0 * sit)] : [0, 0];
      VK.world(cam, () => {
        VK.ground(cam);
        Coaster.drawTrack(tr, vw, { seed: 3 });
        Coaster.drawRuler(vw, 5, { y1: 20, seed: 4, side: 'left', mark: st.y });
        // a dashed line from the cart across to the ruler (its reading is the cart's height, from the sim)
        g.save(); g.setLineDash([12, 10]); g.strokeStyle = 'rgba(251,245,230,0.55)'; g.lineWidth = 3;
        g.beginPath(); g.moveTo(vw.X(st.x) - 40, vw.Y(st.y) - 30); g.lineTo(vw.X(5) + 30, vw.Y(st.y)); g.stroke(); g.restore();
        g.save(); g.translate(j[0], j[1]); Coaster.drawCart(tr, vw, st.s, { h: 54 }); g.restore();
      });
      // very intense sitting: strain lines + sweat
      const [cx, cy] = VK.toScreen(cam, st.x, st.y + 1.4);
      VK.strain(cx, cy, 70 * cam.z, t, t6 - 0.1, sit, 402);
      // "clunk" at the top of the lift
      if (t > tArr && t < tArr + 1.2) hand('clunk.', cx + 70, cy - 90, 46, tArr, { dur: 0.2, color: PAL.cream, outline: OUT, seed: 404, alpha: unpop(t, tArr + 0.9) });
      s2Formula(c, R, top);
      chartAt([1505, 60, 380, 400], st, { ref: 100000, max: 125000, show: { heat: false }, s: pop(t, c.W('s2_04', 'joules')) });
      const stg = VK.stage(t, part, [[0, 'inset']]);
      VK.cast(t, ctx, stg);
      VK.chapter(t, part);
    },
  };
  /* the frozen setup behind the PREDICT card: the cart on the lip (left margin), PE full (right margin) */
  function s2Ask(t, part, ctx, c, R, top) {
    const cam = { x: 31.5, y: 20, z: 1.9, sx: 210, sy: 600 };
    VK.world(cam, () => {
      VK.ground(cam);
      Coaster.drawTrack(R.track, vw, { seed: 3 });
      Coaster.drawCart(R.track, vw, top.s, { h: 54 });
    }, { clouds: false });
    VK.tokens([{ s: 'PE', x: 700, t0: -1e6 }, { s: '=', x: 820, t0: -1e6 }, { s: 'm', x: 920, t0: -1e6 }, { s: 'g', x: 1020, t0: -1e6 }, { s: 'h', x: 1120, t0: -1e6 }], 90, 80, 21);
    chartAt([1552, 190, 340, 400], top, { ref: 100000, max: 125000, show: { heat: false } });
    VK.label('20 m up', 210, 330, 48, -1e6, { dur: 0.01, seed: 405 });
  }
  /* q1 reply: the bars, with the chosen answer; PE turns into KE */
  function s2Reply(t, part, ctx, c, R, top) {
    const id = part.lines[0].id;
    const cue = id === 'q1_wrong0' ? c.W(id, 'speed') : id === 'q1_wrong2' ? c.W(id, 'somewhere') : c.W(id, 'kinetic');
    const cam = { x: 31.5, y: 20, z: 1.5, sx: 1080, sy: 690 };
    VK.world(cam, () => {
      VK.ground(cam);
      Coaster.drawTrack(R.track, vw, { seed: 3 });
      Coaster.drawCart(R.track, vw, top.s, { h: 54 });
    });
    const box = [440, 110, 400, 420];
    chartAt(box, top, { ref: 100000, max: 125000, show: { heat: false } });
    // PE → KE: an arrow from the full PE bar over to the empty KE column
    const slot = (box[2] - 40) / 3, pex = box[0] + 20 + slot * 0.5, kex = box[0] + 20 + slot * 1.5;
    inkArrow(pex + 10, box[1] + 150, kex + 6, box[1] + box[3] - 110, { bulge: -60, w: 7, color: PAL.red, seed: 410, prog: P(t, cue, cue + 0.6) });
    VK.label('PE turns into KE', box[0] + box[2] / 2, box[1] + box[3] + 40, 46, cue + 0.3, { seed: 411, rot: -0.03 });
    const tw = c.W(id, 'Watch');
    if (t > tw) inkCircle(kex, box[1] + box[3] - 90, 52, { sy: 1.3, w: 6, color: PAL.red, seed: 412, prog: P(t, tw, tw + 0.5) });
    VK.answerNote(ctx, part, 1010, 860, 0.35, 0.9, { w: 380 });
    const stg = VK.stage(t, part, [[0, 'two']]);
    VK.cast(t, ctx, stg);
    VK.chapter(t, part);
  }

  /* ======================================================================
     s3 — the drop: live frictionless sim, chart + gauge, a freeze, a rewind,
          a slow-motion replay to the bottom, KE = ½ m v², v = 20 m/s
     ====================================================================== */
  const S3_A = 2.35;                        // the slow-mo replay starts just before the steep part
  function s3Times(c, R) {
    const tR = c.L('s3_01') + 0.45;                                         // release
    const tF = Math.min(c.W('s3_02', 'motion') + 0.2, tR + R.sim.when(p => p.x >= R.marks.camel - 1.5));   // freeze by the camel-back crest
    const tRw = c.L('s3_04'), tSl = tRw + 1.1;                               // rewind, then slow-mo ½×
    const tB = R.marks.tBottom, tBot = tSl + (tB - S3_A) / 0.5;               // the replay reaches the bottom
    return { tR, tF, tauF: tF - tR, tRw, tSl, tB, tBot };
  }
  function s3tau(t, c, R) {
    const k = s3Times(c, R);
    if (t < k.tR) return 0;
    if (t < k.tF) return t - k.tR;
    if (t < k.tRw) return k.tauF;
    if (t < k.tSl) return lerp(k.tauF, S3_A, E.inOutSine(P(t, k.tRw, k.tSl)));
    return Math.min(k.tB, S3_A + 0.5 * (t - k.tSl));
  }
  /* the s3 camera at part time u (a pure function, so the bubble can look ahead along the ride) */
  function s3Cam(u, c, R, k) {
    const st = R.sim.sample(s3tau(u, c, R));
    const fx = VK.smooth(v => { const s = R.sim.sample(s3tau(v, c, R)); return s.x + s.v * 0.25; }, u, 0.7);
    return VK.cam([
      VK.frame(fx + 3, 1005, 1.55),
      VK.frame(fx + 9, 975, 1.1, k.tR + 2.3, 1.6),        // the drop: pull back, keep the cart left of the bubble
      VK.frame(fx, 965, 1.3, k.tF - 0.1, 0.9),             // freeze frame
      VK.frame(fx + 2, 985, 1.35, k.tSl - 0.5, 0.9),       // slow-mo replay
      VK.frame(st.x, 925, 1.55, k.tBot - 0.3, 1.0),        // frozen at the bottom
    ], u);
  }
  /* where the action is on screen at part time u: the cart (+ its film tag and speed lines) and the working */
  function s3Action(u, c, R, k) {
    const cam = s3Cam(u, c, R, k), st = R.sim.sample(s3tau(u, c, R)), [x, y] = VK.toScreen(cam, st.x, st.y);
    const out = [[x - 150, y - 120, x + 150, y + 30], [x - 190, Math.max(420, y - 150) - 45, x + 190, Math.max(420, y - 150) + 45]];
    // the stretch of track the cart is about to ride, where it is on screen NOW (up to 25 m ahead)
    for (let d = 3; d <= 25; d += 3) {
      const p = R.track.at(Math.min(R.track.length, st.s + d)), [px, py] = VK.toScreen(cam, p.x, p.y);
      out.push([px - 70, py - 90, px + 70, py + 20]);
    }
    if (u > c.W('s3_05', 'Solve')) out.push([560, 280, 1360, 580]);
    return out;
  }
  S.s3 = {
    get timing() { return { lead: { lines: C.lead }, gaps: { s3_02: 1.6 }, hold: { lines: 1.2 } }; },
    sfx(part, ctx) {
      const c = VK.cues(ctx), R = Tracks.get('s3'), k = s3Times(c, R), out = VK.chapterSfx(part);
      out.push({ name: 'rumble', t: k.tR + 0.1, loop: true, dur: k.tF - k.tR - 0.1, gain: 0.5 });
      out.push({ name: 'wind', t: k.tR + 2.3, loop: true, dur: k.tF - k.tR - 2.3, gain: 0.4 });
      out.push({ name: 'screams', t: k.tR + 2.7, gain: 0.55 });
      out.push({ name: 'thunk', t: k.tF, gain: 0.5 });
      out.push({ name: 'whoosh', t: k.tRw, gain: 0.5, rate: 0.7 });
      out.push({ name: 'rumble', t: k.tSl, loop: true, dur: k.tBot - k.tSl, gain: 0.35, rate: 0.6 });
      out.push({ name: 'wind', t: k.tSl + 0.8, loop: true, dur: k.tBot - k.tSl - 0.8, gain: 0.25, rate: 0.6 });
      out.push({ name: 'thunk', t: k.tBot, gain: 0.45 });
      out.push({ name: 'ding', t: c.W('s3_02', 'squared') + 0.2, gain: 0.4 });
      out.push({ name: 'scribble', t: c.W('s3_05', 'Solve'), gain: 0.3 }, { name: 'scribble', t: c.W('s3_05', 'v'), gain: 0.3 });
      out.push({ name: 'ding', t: c.W('s3_05', 'twenty') + 0.3, gain: 0.45 });
      out.push({ name: 'pop', t: c.W('s3_06', 'seventy'), gain: 0.4 });
      return out;
    },
    draw(t, part, ctx) {
      const c = VK.cues(ctx), R = Tracks.get('s3'), tr = R.track, k = s3Times(c, R);
      const tau = s3tau(t, c, R), st = R.sim.sample(tau);
      const cam = s3Cam(t, c, R, k);
      const sh = t < k.tF ? VK.shake(t, k.tR + k.tB - 0.05, 16, 0.6, 5) : VK.shake(t, k.tBot - 0.05, 8, 0.45, 6);
      VK.world(cam, () => {
        VK.ground(cam);
        Coaster.drawTrack(tr, vw, { seed: 3 });
        if (t > k.tRw && t < k.tSl) {    // rewind ghosts
          for (let i = 1; i <= 3; i++) { g.save(); g.globalAlpha *= 0.18 * (4 - i); Coaster.drawCart(tr, vw, R.sim.sample(s3tau(t - i * 0.05, c, R)).s, { h: 50 }); g.restore(); }
        }
        Coaster.drawCart(tr, vw, st.s, { h: 50 });
      }, { sh });
      const [cx, cy] = VK.toScreen(cam, st.x, st.y);
      if (st.speed > 11 && (t < k.tF || (t > k.tSl && t < k.tBot))) speedLines(cx - 30, cy - 40, Math.cos(st.angle), -Math.sin(st.angle), 80, 50, 4, 90 + (BF % 3), 0.7);
      // film tags above the cart
      const tagY = Math.max(420, cy - 150);
      VK.filmTag('❚❚ FREEZE', cx, tagY, pop(t, k.tF, 0.3) * unpop(t, k.tRw), PAL.navy);
      VK.filmTag('◀◀ REWIND', cx, tagY, pop(t, k.tRw, 0.25) * unpop(t, k.tSl), PAL.red);
      VK.filmTag('▶ SLOW-MO ×½', cx, tagY, pop(t, k.tSl, 0.25) * unpop(t, k.tBot), PAL.teal);
      VK.filmTag('❚❚ THE BOTTOM', cx, tagY, pop(t, k.tBot, 0.3), PAL.navy);
      // KE = ½ m v² on "K E equals one half m v squared"
      VK.tokens([{ s: 'KE', x: 700, t0: c.W('s3_02', 'K') }, { s: '=', x: 830, t0: c.W('s3_02', 'equals') },
        { s: '½', x: 935, t0: c.W('s3_02', 'one') }, { s: 'm', x: 1040, t0: c.W('s3_02', 'm') }, { s: 'v²', x: 1165, t0: c.W('s3_02', 'v') }], 116, 94, 131);
      VK.label('mass', 1040, 214, 38, c.W('s3_02', 'm') + 0.2, { seed: 132 });
      VK.label('speed', 1150, 214, 38, c.W('s3_02', 'v') + 0.1, { seed: 133 });
      if (t > c.W('s3_02', 'squared')) inkCircle(1205, 92, 34, { w: 5, color: PAL.red, seed: 138, prog: P(t, c.W('s3_02', 'squared'), c.W('s3_02', 'squared') + 0.4) });
      // the working at the bottom (s3_05): numbers straight from the sim
      const bot = R.sim.sample(k.tB), v2 = Math.round(2 * bot.KE / bot.m), vB = Math.round(bot.speed);
      const tS = c.W('s3_05', 'Solve'), tV = c.W('s3_05', 'v'), t20 = c.W('s3_05', 'twenty');
      VK.label(`${Coaster.fmtJ(bot.total0).replace(' J', '')} = ½ × ${bot.m} × v²`, 960, 330, 64, tS, { seed: 134 });
      VK.label(`v² = ${v2}`, 960, 420, 64, tV, { seed: 135 });
      VK.chip(`v = ${vB} m/s`, 880, 520, 58, t20, { seed: 136, color: PAL.mustard, spin: 0.5 });
      VK.chip(`= ${Math.round(Coaster.kmh(bot.speed))} km/h`, 1215, 520, 58, c.W('s3_06', 'seventy'), { seed: 137, color: PAL.red, ink: PAL.cream, spin: -0.5 });
      // HUD: chart top-left, gauge top-right
      const tTot = c.W('s3_03', 'total');
      chartAt(CHART_L, st, { ref: st.total0, max: 125000, show: { heat: false }, circleTotal: P(t, tTot, tTot + 0.6) });
      const gp = 1 + 0.18 * VK.win(t, c.W('s3_06', 'seventy') - 0.1, c.E('s3_06'), 0.25);
      g.save(); g.translate(1782, 190); g.scale(gp, gp); g.translate(-1782, -190);
      Coaster.drawGauge(1782, 150, 92, st.v);
      g.restore();
      const stg = VK.stage(t, part, [[0, 'inset']]);
      VK.cast(t, ctx, stg, { max: t > c.L('s3_03') && t < c.E('s3_03') + 0.4 ? 'point' : null },
        { mode: 'corridor', avoid: u => s3Action(u, c, R, k), key: 's3' });
      VK.chapter(t, part);
    },
  };

  /* ======================================================================
     s4 — the mass race: 50 kg vs 5000 kg (twin sims), a photo-finish tie,
          then m g h = ½ m v² with both m's crossed out in red
     ====================================================================== */
  const LANE = 600;                                     // world px between the two lanes' ground lines
  const laneA = Coaster.view(0, -LANE, K), laneB = vw;
  const tieT = () => Tracks.get('s4').marks.tBottomMax;
  function s4race(t, part) {                             // race time (sim) in any s4 part
    if (part.kind === 'lines') return 0;
    if (part.kind === 'after') return tieT();
    return clamp(t - (lastLine(part).end + 0.3), 0, tieT());
  }
  /* both lanes, finish line and carts (world px); the carts are always parked there.
     o: {tau, maxIn, eleIn (rider hop-in 0..1), eleSquash, erase, jit, only: 'A'|'B', h (cart height)} */
  function s4lanes(o) {
    const R = Tracks.get('s4'), tr = R.track, tau = o.tau ?? 0;
    [[laneA, 3, 'A'], [laneB, 4, 'B']].forEach(([v, seed, id]) => {
      if (o.only && o.only !== id) return;
      const bh = id === 'A' ? 56 : 150;
      paperRect(v.X(40), v.Y(0) + bh / 2 - 4, 3400, bh, PAL.kraft, { seed: 300 + seed, torn: true, tex: 'tex_kraft', depth: 1.1, boil: false });
      Coaster.drawTrack(tr, v, { seed, postEvery: 3.5, s1: tr.sAtX(70) });
      // finish line at the bottom of the drop: a post and a chequered flag
      const X = v.X(28), Y = v.Y(0);
      paperRect(X, Y - 70, 8, 140, PAL.white, { seed: seed + 20, depth: 0.6, boil: false });
      for (let i = 0; i < 4; i++) for (let j = 0; j < 3; j++) { g.fillStyle = (i + j) % 2 ? PAL.ink : PAL.white; g.fillRect(X + 4 + i * 14, Y - 140 + j * 14, 14, 14); }
    });
    const sm = R.sims.max.sample(tau), se = R.sims.elephant.sample(tau);
    const h = o.h ?? S4_CART;
    const pm = o.only !== 'B' ? cartRider(tr, laneA, sm.s, { h, rider: 'max_cross', hop: o.maxIn ?? 1, seed: 6 }) : null;
    const pe = o.only !== 'A' ? cartRider(tr, laneB, se.s, { h, rider: 'elephant', hop: o.eleIn ?? 1, sx: 1.05 * (o.eleSquash ?? 1), sy: 0.95 / (o.eleSquash ?? 1), erase: o.erase || 0, jx: o.jit ? jit(501, 1.4) : 0, seed: 7 }) : null;
    return { sm, se, pm, pe, tr };
  }
  const S4_FRAME = VK.frame(24, 1000, 0.84);
  const S4_CART = 66;                                   // cart height (world px) in the race
  S.s4 = {
    get timing() { return { lead: { lines: C.lead }, hold: { lines: 1.3, reply: 0.3 + tieT() + 1.9, after: 1.1 }, gaps: { s4_08: 'beat' } }; },
    sfx(part, ctx) {
      const c = VK.cues(ctx), out = VK.chapterSfx(part);
      if (part.kind === 'lines') {
        out.push({ name: 'pop', t: c.W('s4_02', 'fifty'), gain: 0.4 }, { name: 'thunk', t: c.W('s4_02', 'elephant'), gain: 0.6 });
        out.push({ name: 'strain', t: c.L('s4_03') - 0.2, gain: 0.45 });
      } else if (part.kind === 'reply') {
        const go = lastLine(part).end + 0.3;
        out.push({ name: 'rumble', t: go, loop: true, dur: tieT(), gain: 0.5 }, { name: 'wind', t: go + 2, loop: true, dur: tieT() - 2, gain: 0.35 });
        out.push({ name: 'stamp', t: go + tieT(), gain: 0.7 }, { name: 'ding', t: go + tieT() + 0.4, gain: 0.35 });
      } else {
        out.push({ name: 'scribble', t: c.W('s4_07', 'deletes'), gain: 0.45 });
        out.push({ name: 'scribble', t: c.W('s4_08', 'Cross') + 0.2, gain: 0.4 }, { name: 'scribble', t: c.W('s4_08', 'both'), gain: 0.4 });
        out.push({ name: 'ding', t: c.W('s4_08', 'square'), gain: 0.45 });
      }
      return out;
    },
    draw(t, part, ctx) {
      const c = VK.cues(ctx), R = Tracks.get('s4');
      if (ctx.asking) return s4Ask(t, part, ctx);
      if (part.kind === 'after') return s4After(t, part, ctx, c, R);
      const tau = s4race(t, part), tFifty = c.W('s4_02', 'fifty'), tEle = c.W('s4_02', 'elephant'), tFive = c.W('s4_02', 'Five');
      const t3 = c.L('s4_03'), e4 = c.E('s4_04');
      const go = part.kind === 'reply' ? lastLine(part).end + 0.3 : 1e6, tTie = go + tieT();
      const cam = VK.cam([
        S4_FRAME,
        { t: t3 - 0.45, d: 0.9, x: 4.6, y: 21.4, z: 2.5, sx: 960, sy: 520 },   // "where did you get an elephant?"
        Object.assign({}, S4_FRAME, { t: e4 + 0.15, d: 0.8 }),
        Object.assign(VK.frame(20, 1000, 0.86), { t: go, d: tieT() }),            // push in as they race
      ], t);
      const cram = VK.win(t, t3 - 0.3, e4 + 0.1, 0.3);
      let L;
      const hop = t0 => (part.kind === 'lines' ? P(t, t0, t0 + 0.55) : 1);
      VK.world(cam, () => { L = s4lanes({ tau, only: cam.z > 1.6 ? 'B' : null, maxIn: hop(tFifty), eleIn: hop(tEle), eleSquash: 1 + 0.1 * cram * (0.5 + 0.5 * Math.sin(t * 9)), jit: cram > 0 }); },
        { sh: part.kind === 'reply' ? VK.shake(t, tTie - 0.05, 10, 0.5, 8) : [0, 0] });
      // the elephant, crammed: strain lines + sweat
      if (L.pe) { const [ex, ey] = VK.pxToScreen(cam, L.pe.x, L.pe.y - 60); VK.strain(ex, ey, 44 * cam.z, t, t3 - 0.2, cram, 505); }
      // mass tags ride with the carts (numbers from the sims)
      const dz = Math.min(1.8, cam.z / 0.84);
      if (L.pm) { const [x, y] = VK.pxToScreen(cam, L.pm.x, L.pm.y); VK.tag(R.sims.max.m + ' kg', x + 150 * dz, y - 20, 40, pop(t, tFifty + 0.3) * (1 - cram), { seed: 510, color: PAL.sky }); }
      if (L.pe) { const [x, y] = VK.pxToScreen(cam, L.pe.x, L.pe.y); VK.tag(R.sims.elephant.m + ' kg', x + 160 * dz, y - 30 * dz, 40, pop(t, tFive), { seed: 511, color: PAL.pink }); }
      if (part.kind === 'reply') {
        // photo finish: flash, then TIE!
        if (t > tTie) {
          g.save(); g.globalAlpha = 0.85 * (1 - P(t, tTie, tTie + 0.45)); g.fillStyle = '#fffdf2'; g.fillRect(0, 0, 1920, 1080); g.restore();
          UI.stamp('TIE!', 1420, 560, { s: pop(t, tTie + 0.05, 0.3), rot: -0.1 });
          const tm = R.sims.max.timeAtX(R.marks.bottom), te = R.sims.elephant.timeAtX(R.marks.bottom);
          VK.label(`${R.sims.max.m} kg: ${tm.toFixed(2)} s   ·   ${R.sims.elephant.m} kg: ${te.toFixed(2)} s`, 1420, 680, 42, tTie + 0.3, { seed: 512 });
        }
        VK.answerNote(ctx, part, 1640, 175, 0.3, tTie + 0.2, { w: 380 });
      }
      const stg = VK.stage(t, part, part.kind === 'lines' ? [[0, 'two'], [c.W('s4_02', 'test') - 0.2, 'inset']] : [[0, 'inset']]);
      VK.cast(t, ctx, stg, { max: t > t3 - 0.1 && t < c.E('s4_03') + 0.3 ? 'point' : null, voss: t > c.W('s4_02', 'Your') && t < c.E('s4_02') ? 'point' : null });
      VK.chapter(t, part);
    },
  };
  /* PREDICT backdrop: each cart in its own snapshot at the screen edges, tagged */
  function s4Ask() {
    const R = Tracks.get('s4');
    VK.sky(null, { clouds: false });
    [[195, 'A', R.sims.max.m, PAL.sky], [1725, 'B', R.sims.elephant.m, PAL.pink]].forEach(([x, lane, m, col], i) => {
      g.save(); g.translate(x, 560); g.rotate(i ? 0.03 : -0.03);
      paperRect(0, 0, 350, 520, PAL.white, { seed: 520 + i, depth: 1.6 });
      g.beginPath(); g.rect(-155, -240, 310, 400); g.clip();
      g.fillStyle = paperTile('#2f5f8f', 9); g.fillRect(-160, -250, 320, 420);
      const cam = { x: 5.5, y: 18, z: 1.35, sx: 0, sy: 0 };
      g.save(); VK.apply(cam); g.translate(0, lane === 'A' ? LANE : 0);
      s4lanes({ tau: 0, only: lane });
      g.restore(); g.restore();
      VK.tag(m + ' kg', x, 790, 48, 1, { color: col, seed: 530 + i });
      tape(x, 310, 130, i ? 0.08 : -0.08, undefined, 540 + i);
    });
    VK.label('same 20 m drop · no friction', 960, 100, 52, -1e6, { dur: 0.01, seed: 545 });
  }
  /* the polaroid of the photo finish (optionally with the elephant being erased) */
  function polaroid(x, y, s, rot, erase, stampS) {
    if (s <= 0) return;
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    paperRect(0, 30, 640, 520, PAL.white, { seed: 560, depth: 1.8 });
    // a photo-finish strip per lane, close on the finish line, so both riders read at polaroid size
    [['A', -26, -190], ['B', 184, 10]].forEach(([id, gy, y0]) => {
      g.save(); g.beginPath(); g.rect(-296, y0, 592, 200); g.clip();
      g.fillStyle = paperTile('#2f5f8f', 9); g.fillRect(-300, y0 - 2, 600, 204);
      g.translate(0, gy); g.scale(0.8, 0.8); g.translate(-vw.X(Tracks.get('s4').marks.bottom - 1.5), id === 'A' ? LANE : 0);
      s4lanes({ tau: tieT(), erase, only: id });
      g.restore();
    });
    ink([[-296, 10], [296, 10]], { w: 4, color: PAL.cream, seed: 563, passes: 1 });
    hand('photo finish', 0, 250, 44, -1e6, { dur: 0.01, color: PAL.navy, seed: 561, font: "'Gochi Hand'" });
    if (stampS > 0) UI.stamp('TIE!', -185, 10, { s: 0.8 * stampS, rot: -0.14 });   // on the seam, clear of both riders
    g.restore();
    tape(x - 180 * s, y - 210 * s, 150 * s, -0.5, undefined, 562);
  }
  function s4After(t, part, ctx, c, R) {
    const t8 = c.L('s4_08'), tDel = c.W('s4_07', 'deletes');
    VK.sky(null);
    Levels.title.ground(1000);
    // the photo: bottom-centre during the two-shot, then pinned up in the corner
    const mv = E.inOutCubic(P(t, t8 - 0.8, t8 + 0.1)), ps = lerp(0.92, 0.52, mv), px = lerp(960, 250, mv), py = lerp(790, 240, mv);
    const erase = E.inOutSine(P(t, tDel, tDel + 1.1));
    polaroid(px, py, ps, lerp(-0.03, -0.06, mv), erase, 1);
    if (erase > 0 && erase < 1) {          // the eraser scrubbing across the photo
      const ex = px + lerp(-170, 200, erase) * ps, ey = py + (150 + Math.sin(t * 40) * 16) * ps;
      paperRect(ex, ey, 120, 64, PAL.pink, { seed: 570, depth: 1.3, rot: 0.4 });
      paperRect(ex - 30, ey - 17, 54, 64, PAL.sky, { seed: 571, depth: 0, rot: 0.4 });
    }
    // s4_06: ×100 the energy … ×100 the mass to get moving … cancels
    const pm = R.sims.max.total0, pe = R.sims.elephant.total0, ratio = Math.round(R.sims.elephant.m / R.sims.max.m);
    const tSt = c.W('s4_06', 'stores'), tNeeds = c.W('s4_06', 'needs'), tCan = c.W('s4_06', 'cancels'), a6 = unpop(t, t8 - 0.8);
    if (a6 > 0) {
      g.save(); g.globalAlpha *= a6;
      VK.label(`energy: ${Coaster.fmtJ(pm)} vs ${Coaster.fmtJ(pe)}  (×${ratio})`, 960, 62, 46, tSt, { seed: 580 });
      VK.label(`mass to get moving: ${R.sims.max.m} kg vs ${R.sims.elephant.m} kg  (×${ratio})`, 960, 130, 46, tNeeds, { seed: 581 });
      g.restore();
      if (t > tCan) {
        g.save(); g.globalAlpha *= a6;
        VK.label('same speed!', 960, 205, 56, tCan + 0.2, { color: PAL.mustard, seed: 584, rot: -0.03 });
        g.restore();
      }
    }
    // s4_08: m g h = ½ m v², cross out the m's, v = √(2 g h)
    const Y1 = 330, Y2 = 530, sz = 100, xs = [560, 680, 800, 935, 1065, 1185, 1320];
    const w8 = (w, n = 1) => c.W('s4_08', w, n);
    VK.tokens([{ s: 'm', x: xs[0], t0: w8('m') }, { s: 'g', x: xs[1], t0: w8('g') }, { s: 'h', x: xs[2], t0: w8('h') }, { s: '=', x: xs[3], t0: w8('equals') },
      { s: '½', x: xs[4], t0: w8('one') }, { s: 'm', x: xs[5], t0: w8('m', 2) }, { s: 'v²', x: xs[6], t0: w8('v') }], Y1, sz, 601);
    const tX = w8('Cross') + 0.25, tB = w8('both');
    VK.strike(xs[0] - 55, Y1 + 50, xs[0] + 55, Y1 - 50, P(t, tX, tX + 0.3), 610, PAL.red, 12);
    VK.strike(xs[5] - 55, Y1 + 50, xs[5] + 55, Y1 - 50, P(t, tB, tB + 0.3), 611, PAL.red, 12);
    const tSq = w8('square');
    VK.tokens([{ s: 'v', x: 700, t0: w8('v', 2) }, { s: '=', x: 820, t0: w8('v', 2) + 0.15 }, { s: '2', x: 1010, t0: w8('two') }, { s: 'g', x: 1120, t0: w8('g', 2) }, { s: 'h', x: 1230, t0: w8('h', 2) }], Y2, sz, 620);
    ink([[900, Y2 + 6], [928, Y2 - 8], [956, Y2 + 62], [986, Y2 - 78], [1300, Y2 - 78]], { w: 8, color: PAL.cream, seed: 625, prog: P(t, tSq, tSq + 0.55), step: 10 });
    VK.label('no mass anywhere!', 1000, 650, 54, w8('No'), { color: PAL.mustard, seed: 626, rot: -0.03 });
    // s4_09: air resistance — a tiny bit, not ×100
    const t9 = c.L('s4_09'), n9 = pop(t, t9 + 0.3, 0.35);
    if (n9 > 0) {
      g.save(); g.translate(600, 830); g.rotate(0.03); g.scale(n9, n9);
      paperRect(0, 0, 470, 230, PAL.cream, { seed: 630, depth: 1.4 });
      g.restore();
      tape(600, 722, 120, -0.04, undefined, 631);
      hand('real life: air resistance', 600, 760, 40, t9 + 0.4, { color: PAL.navy, seed: 632 });
      const tTiny = c.W('s4_09', 'tiny');
      // two little carts at a finish line, the heavy one a hair ahead
      ink([[680, 790], [680, 905]], { w: 4, color: PAL.ink, seed: 633, passes: 1 });
      paperRect(642, 822, 64, 28, PAL.sky, { seed: 634, depth: 0.5 });
      paperRect(647, 876, 64, 28, PAL.pink, { seed: 635, depth: 0.5 });
      if (t > tTiny) { inkCircle(680, 878, 34, { w: 4, color: PAL.red, seed: 636, prog: P(t, tTiny, tTiny + 0.4) }); hand('a tiny bit', 480, 858, 40, tTiny + 0.2, { color: PAL.red, seed: 637 }); }
      hand(`not ×${ratio}!`, 770, 912, 38, c.W('s4_09', 'Not'), { color: PAL.red, seed: 638, rot: -0.05 });
    }
    const stg = VK.stage(t, part, [[0, 'two'], [t8 - 0.7, 'inset']]);
    VK.cast(t, ctx, stg, { voss: t > c.W('s4_08', 'Cross') - 0.3 && t < c.E('s4_08') ? 'point' : null });
    VK.chapter(t, part);
  }

  /* ======================================================================
     s5 — speed squared: 30 → 60 km/h makes 1 → 4 blocks of KE; skids ×4
     ====================================================================== */
  const ROWS = [{ v: 30, y: 300 }, { v: 60, y: 520 }];
  const S5 = { carX: 440, blockX: 780, block: 104, pxm: 55, a: 7 };      // a: braking deceleration (m/s²)
  function road(y, off, seed) {
    paperRect(960, y + 44, 2040, 70, '#7a6048', { seed, depth: 0.8, tex: 'tex_kraft', boil: false });
    g.save(); g.fillStyle = 'rgba(251,245,230,0.85)';
    for (let x = -((off % 160) + 160) % 160; x < 1960; x += 160) g.fillRect(x, y + 41, 70, 7);
    g.restore();
  }
  /* the engraved family car (car.png, faces right): width on screen and where its tyres touch the road */
  const CAR_FIT = { w: 300, base: 50, dx: 0 };
  function paperCar(x, y, dist, seed, o = {}) {
    const s = o.s ?? 1; if (s <= 0) return;
    g.save(); g.translate(x, y); g.scale(s, s);
    if (IMG.car) {
      const im = IMG.car, h = CAR_FIT.w * im.height / im.width;
      g.save(); g.translate(CAR_FIT.dx, CAR_FIT.base); g.rotate(o.rot || 0);
      cut('car', 0, 0, h, { ax: 0.5, ay: 1, depth: 1.1, seed });
      g.restore();
      if (o.plate > 0) {
        g.save(); g.translate(CAR_FIT.w * 0.36, -h * 0.55); g.rotate(0.1); g.scale(o.plate, o.plate);
        paperRect(0, 0, 50, 50, PAL.white, { seed: seed + 9, depth: 0.8, outline: true });
        g.font = "40px 'Luckiest Guy'"; g.fillStyle = PAL.red; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('L', 0, 3);
        g.restore();
      }
      g.restore();
      return;
    }
    paperRect(0, -10, 230, 58, o.color || PAL.red, { seed, depth: 1.1, outline: true });
    paperPiece([[-70, -38], [-40, -80], [50, -80], [80, -38]], 0, 0, o.color || PAL.red, { seed: seed + 1, depth: 0.8, outline: true });
    paperPiece([[-52, -42], [-32, -72], [40, -72], [62, -42]], 0, 0, PAL.sky, { seed: seed + 2, depth: 0 });
    [-70, 70].forEach((wx, i) => {
      paperCircle(wx, 22, 26, PAL.ink, { seed: seed + 3 + i, depth: 0.6 });
      g.save(); g.translate(wx, 22); g.rotate(dist / 26); g.strokeStyle = PAL.cream; g.lineWidth = 3;
      g.beginPath(); g.moveTo(-14, 0); g.lineTo(14, 0); g.moveTo(0, -14); g.lineTo(0, 14); g.stroke(); g.restore();
    });
    if (o.plate > 0) {                                   // the learner plate (driving lesson)
      g.save(); g.translate(96, -30); g.rotate(0.1); g.scale(o.plate, o.plate);
      paperRect(0, 0, 50, 50, PAL.white, { seed: seed + 9, depth: 0.8, outline: true });
      g.font = "40px 'Luckiest Guy'"; g.fillStyle = PAL.red; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText('L', 0, 3);
      g.restore();
    }
    g.restore();
  }
  function keBlock(x, y, s, seed, label = 'KE', color = PAL.red) {
    if (s <= 0) return;
    g.save(); g.translate(x, y); g.scale(s, s);
    paperRect(0, 0, S5.block, S5.block, color, { seed, depth: 0.9, outline: true });
    g.font = "34px 'Luckiest Guy'"; g.fillStyle = PAL.cream; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(label, 0, 3);
    g.restore();
  }
  function qBlock(x, y, s) {
    if (s <= 0) return;
    g.save(); g.translate(x, y); g.scale(s, s);
    g.setLineDash([12, 8]); g.lineDashOffset = -(BF % 4) * 5; g.strokeStyle = PAL.cream; g.lineWidth = 4;
    g.strokeRect(-S5.block / 2, -S5.block / 2, S5.block, S5.block); g.restore();
    ransom('?', x, y, 70 * s, -1e6, { seed: 540 });
  }
  /* braking: constant deceleration a from v (km/h) — honest kinematics */
  const brake = (kmh, tb) => { const v = kmh / 3.6, ts = v / S5.a, u = clamp(tb, 0, ts); return { d: v * u - 0.5 * S5.a * u * u, dStop: v * v / (2 * S5.a), tStop: ts, done: tb >= ts }; };
  /* when each of the four row-2 blocks pops in the reply (the answer decides the choreography) */
  function s5blockTimes(part, c) {
    const id = part.lines[0].id;
    if (id === 'q3_wrong0') { const a = c.W(id, 'two'), b = c.W(id, 'two', 2); return [a, a + 0.15, b, b + 0.15]; }
    const f = c.W(id, 'four'); return [f, f + 0.15, f + 0.3, f + 0.45];
  }
  S.s5 = {
    get timing() { return { lead: { lines: C.lead }, hold: { lines: 1.3, reply: 1.2, after: 1.1 } }; },
    sfx(part, ctx) {
      const c = VK.cues(ctx), out = VK.chapterSfx(part);
      if (part.kind === 'lines') {
        out.push({ name: 'pop', t: c.W('s5_01', 'one'), gain: 0.35 }, { name: 'scribble', t: c.W('s5_01', 'squared'), gain: 0.35 });
        out.push({ name: 'rolling', t: c.W('s5_02', 'speed'), loop: true, dur: 4, gain: 0.3 }, { name: 'whoosh', t: c.W('s5_02', 'double'), gain: 0.45 });
      } else if (part.kind === 'reply') {
        s5blockTimes(part, c).forEach(t0 => out.push({ name: 'pop', t: t0, gain: 0.4 }));
      } else {
        const tb = c.W('s5_03', 'braking');
        out.push({ name: 'brakes', t: tb, gain: 0.55 });
        out.push({ name: 'stamp', t: tb + brake(ROWS[1].v, 0).tStop + 0.1, gain: 0.5 });
        out.push({ name: 'pop', t: c.W('s5_04', 'driving'), gain: 0.5 });
      }
      return out;
    },
    draw(t, part, ctx) {
      const c = VK.cues(ctx);
      if (ctx.asking) return s5Ask();
      VK.sky(null);
      Levels.title.ground(1000);
      const tSp = c.W('s5_02', 'speed'), tDbl = c.W('s5_02', 'double');
      const tBr = c.W('s5_03', 'braking'), tBrakes = c.W('s5_03', 'brakes');
      // KE = ½ m v² with the square circled on "squared"
      const t1 = c.W('s5_01', 'one');
      VK.tokens([{ s: 'KE', x: 700, t0: t1 - 0.4 }, { s: '=', x: 830, t0: t1 - 0.25 }, { s: '½', x: 935, t0: t1 },
        { s: 'm', x: 1040, t0: c.W('s5_01', 'm') }, { s: 'v²', x: 1165, t0: c.W('s5_01', 'v') }], 110, 90, 701);
      const tSq = c.W('s5_01', 'squared');
      if (t > tSq) inkCircle(1192, 88, 38, { w: 6, color: PAL.red, seed: 702, prog: P(t, tSq, tSq + 0.4) });
      const bt = part.kind === 'reply' ? s5blockTimes(part, c) : null;
      const b4 = i => (part.kind === 'lines' ? 0 : part.kind === 'after' ? 1 : pop(t, bt[i]));
      const blocksOut = part.kind === 'after' ? unpop(t, tBrakes, 0.35) : 1;
      ROWS.forEach((r, k) => {
        const appear = part.kind === 'lines' ? (k === 0 ? tSp : tDbl) : -1e6;
        const s = pop(t, appear, 0.4); if (s <= 0) return;
        const braking = part.kind === 'after' && t > tBr;
        const br = braking ? brake(r.v, t - tBr) : { d: 0, done: false };
        // cruising: the road scrolls under the car; braking: the road stops and the car slides on
        const cruiseT = braking ? tBr : t;
        road(r.y, (r.v / 3.6) * S5.pxm * cruiseT, 710 + k);
        const x = S5.carX + br.d * S5.pxm;
        if (braking) {
          ink([[S5.carX - 70, r.y + 46], [x - 70, r.y + 46]], { w: 12, color: 'rgba(30,24,20,0.85)', seed: 720 + k, passes: 1, step: 20, amp: 0.8 });
          ink([[S5.carX + 70, r.y + 42], [x + 70, r.y + 42]], { w: 8, color: 'rgba(30,24,20,0.6)', seed: 722 + k, passes: 1, step: 20, amp: 0.8 });
        }
        if (part.kind === 'after' && t > tBr - 0.4) { ink([[S5.carX, r.y - 70], [S5.carX, r.y + 76]], { w: 5, color: PAL.cream, seed: 725 + k, passes: 1 }); hand('brake!', S5.carX - 14, r.y - 52, 34, tBr - 0.4, { align: 'right', color: PAL.cream, outline: OUT, seed: 726 + k }); }
        paperCar(x, r.y, (r.v / 3.6) * S5.pxm * cruiseT + br.d * S5.pxm, 730 + k * 7, { s, rot: braking && !br.done ? 0.025 : 0, plate: k === 1 ? pop(t, c.W('s5_04', 'driving'), 0.35) : 0 });
        VK.label(r.v + ' km/h', 170, r.y - 12, 58, appear + 0.1, { seed: 740 + k });
        // KE blocks: row 1 has one; row 2 has "?" until the answer, then four
        g.save(); g.globalAlpha *= blocksOut;
        if (k === 0) keBlock(S5.blockX, r.y - 20, pop(t, appear + 0.4), 750);
        else if (part.kind === 'lines') qBlock(S5.blockX, r.y - 20, pop(t, tDbl + 0.5));
        else for (let i = 0; i < 4; i++) keBlock(S5.blockX + i * (S5.block + 14), r.y - 20, b4(i), 751 + i);
        g.restore();
        if (k === 1 && part.kind === 'lines') VK.label('× 2 speed', 170, r.y + 112, 40, tDbl + 0.2, { color: PAL.mustard, seed: 745 });
        // the stopping distance, once it has stopped
        if (braking && br.done) {
          const x1 = S5.carX, x2 = S5.carX + br.dStop * S5.pxm, yy = r.y + 96;
          ink([[x1, yy], [x2, yy]], { w: 4, color: PAL.cream, seed: 760 + k, passes: 1 });
          ink([[x1, yy - 12], [x1, yy + 12]], { w: 4, color: PAL.cream, seed: 762 + k, passes: 1 });
          ink([[x2, yy - 12], [x2, yy + 12]], { w: 4, color: PAL.cream, seed: 764 + k, passes: 1 });
          VK.label('skid ≈ ' + Math.round(br.dStop) + ' m', (x1 + x2) / 2, yy + 34, 42, tBr + br.tStop, { dur: 0.3, seed: 766 + k });
        }
      });
      // v² bookkeeping: 30² = 900, 60² = 3,600 = 4 × 900
      if (part.kind !== 'lines') {
        const a2 = ROWS[0].v ** 2, b2 = ROWS[1].v ** 2, tA = part.kind === 'reply' ? 0.4 : -1e6;
        const tBx = part.kind === 'reply' ? bt[3] : -1e6;
        g.save(); g.globalAlpha *= blocksOut;
        VK.label(`${ROWS[0].v}² = ${a2}`, 1440, ROWS[0].y - 20, 54, tA, { seed: 770 });
        VK.label(`${ROWS[1].v}² = ${b2.toLocaleString('en-US')}`, 1440, ROWS[1].y - 50, 54, tBx, { seed: 771 });
        VK.label(`= ${b2 / a2} × ${a2}`, 1440, ROWS[1].y + 20, 54, tBx + 0.4, { color: PAL.mustard, seed: 772 });
        g.restore();
      }
      if (part.kind === 'after') {
        const b1 = brake(ROWS[0].v, 1e9), b2 = brake(ROWS[1].v, 1e9);
        UI.stamp(`${Math.round(b2.dStop / b1.dStop)}× AS FAR`, 1300, 770, { s: pop(t, tBr + b2.tStop + 0.1, 0.3), rot: -0.08 });
      }
      if (part.kind === 'reply') VK.answerNote(ctx, part, 1660, 170, 0.3, bt[3], { w: 360 });
      const stg = VK.stage(t, part, part.kind === 'lines' ? [[0, 'two'], [c.L('s5_02') - 0.2, 'inset']] : [[0, 'inset']]);
      VK.cast(t, ctx, stg);
      VK.chapter(t, part);
    },
  };
  function s5Ask() {
    VK.sky(null, { clouds: false });
    Levels.title.ground(1000);
    road(420, 0, 780);
    [[190, 30, 0], [1730, 60, 1]].forEach(([x, v, k]) => {
      paperCar(x, 420, 0, 790 + k * 7, { s: 0.9 });
      VK.label(v + ' km/h', x, 300, 64, -1e6, { dur: 0.01, seed: 795 + k });
      if (k === 0) keBlock(x, 620, 1, 796); else qBlock(x, 620, 1);
    });
    VK.label('double the speed…', 960, 100, 56, -1e6, { dur: 0.01, seed: 798 });
  }

  /* ======================================================================
     s6 — the real world: friction, the heat bar, warm wheels, and the 25 m
          challenge hill the cart can never climb
     ====================================================================== */
  function s6Times(c) {
    const tR = c.L('s6_01') + 0.7;
    const tF = c.W('s6_03', 'Touch');                      // freeze the ride: you can't touch moving wheels
    return { tR, tF, tauF: Math.max(0, tF - tR) };
  }
  /* a ctx-like cue source for a part we only built (not playing) */
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9½²]/g, '');
  function cuesOf(part) {
    const W = (id, w, n = 1) => {
      const l = part.byId[id]; if (!l) return 1e6;
      for (const pass of [0, 1]) { let k = 0; for (const tk of l.tokens) { const x = normW(tk.w); if ((pass ? x.startsWith(normW(w)) : x === normW(w)) && ++k === n) return l.start + tk.s; } }
      return l.start;
    };
    return { L: id => (part.byId[id] ? part.byId[id].start : 1e6), E: id => (part.byId[id] ? part.byId[id].end : 1e6), W };
  }
  let memo = { at: null, v: {} };                              // per-frame cache (pure: same frame, same inputs)
  function once(key, f) { if (memo.at !== Game.time) memo = { at: Game.time, v: {} }; if (!(key in memo.v)) memo.v[key] = f(); return memo.v[key]; }
  const s6lineTimes = seg => once('s6lines', () => s6Times(cuesOf(Player.build(seg, 'lines'))));
  const S6_GO = 0.25;                                       // the ride resumes this long into the reply
  const s6ans = part => part.answer ?? (Player.current && Player.current.answers.q4) ?? part.seg.question.correct;
  function s6tau(t, part, c) {
    const R = Tracks.get('s6');
    if (part.kind === 'lines') { const k = s6Times(c); return t < k.tR ? 0 : Math.min(t - k.tR, k.tauF); }
    const k = s6lineTimes(part.seg);
    if (part.kind === 'reply') return k.tauF + Math.max(0, t - S6_GO);
    const rp = once('s6reply', () => Player.build(part.seg, 'reply', s6ans(part)));
    return Math.min(R.sim.duration, k.tauF + (rp.dur - S6_GO) + t);
  }
  const k0 = part => s6lineTimes(part.seg).tauF;
  const tStall0 = part => S6_GO + Tracks.get('s6').sim.turns[0].t - k0(part);
  /* the s6 camera at part time u (pure, so the bubble can look ahead along the ride) */
  function s6Cam(u, part, c) {
    const R = Tracks.get('s6'), st = R.sim.sample(s6tau(u, part, c));
    const fx = VK.smooth(v => { const s = R.sim.sample(s6tau(v, part, c)); return s.x + s.v * 0.3; }, u, 0.8);
    const lines = part.kind === 'lines', k = lines ? s6Times(c) : null;
    const keys = [VK.frame(fx + 4, 960, 1.05)];
    if (lines) {
      keys.push(VK.frame(fx + 3, 955, 0.95, k.tR + 1.5, 1.4));
      keys.push({ t: c.W('s6_03', 'wheels') - 0.5, d: 1.0, x: st.x + 0.4, y: st.y + 0.2, z: 4.2, sx: 960, sy: 560 });   // the warm wheels
      keys.push(VK.frame(101, 800, 0.41, c.E('s6_04') + 0.35, 1.4));                                                 // the whole story so far
    } else if (part.kind === 'after') {
      keys[0] = VK.frame(fx + 2, 950, 0.95);
      keys.push(VK.frame(108, 770, 0.37, c.L('s6_06') - 0.4, 1.4));                                                  // every hill, side by side
    }
    return VK.cam(keys, u);
  }
  /* the cart on screen at part time u (with room for its height tag / stall tag) */
  function s6Action(u, part, c) {
    const R = Tracks.get('s6'), st = R.sim.sample(s6tau(u, part, c)), cam = s6Cam(u, part, c), [x, y] = VK.toScreen(cam, st.x, st.y);
    const r = 60 + 40 * cam.z;
    return [[x - r * 1.6, y - r * 1.6, x + r * 1.6, y + r * 0.5]];
  }
  S.s6 = {
    get timing() {
      return { lead: { lines: C.lead }, gaps: { s6_02: 2.4 },
        hold: part => (part.kind === 'reply' ? Math.max(1.0, tStall0(part) + 1.8 - lastLine(part).end) : part.kind === 'lines' ? 1.3 : 1.1) };
    },
    sfx(part, ctx) {
      const c = VK.cues(ctx), R = Tracks.get('s6'), out = VK.chapterSfx(part);
      if (part.kind === 'lines') {
        const k = s6Times(c);
        out.push({ name: 'rumble', t: k.tR, loop: true, dur: k.tauF, gain: 0.45 }, { name: 'wind', t: k.tR + 1.5, loop: true, dur: Math.max(0.5, k.tauF - 1.5), gain: 0.25 });
        out.push({ name: 'screams', t: k.tR + 1.2, gain: 0.35 }, { name: 'thunk', t: k.tF, gain: 0.45 });
        out.push({ name: 'sparkle', t: c.W('s6_03', 'wheels') + 0.4, gain: 0.35 });
        out.push({ name: 'ding', t: c.W('s6_05', 'total'), gain: 0.35 });
      } else if (part.kind === 'reply') {
        out.push({ name: 'rumble', t: S6_GO, loop: true, dur: part.dur - S6_GO, gain: 0.45 }, { name: 'strain', t: tStall0(part) - 0.6, gain: 0.4 });
      } else {
        const tau0 = s6tau(0, part, c), left = Math.max(0, (R.sim.tSettle || tau0) - tau0);
        if (left > 0) out.push({ name: 'rumble', t: 0, loop: true, dur: left, gain: 0.3 });
        out.push({ name: 'stamp', t: c.W('s6_07', 'fight'), gain: 0.5 });
        [['coaster', 1], ['bike', 1], ['skateboard', 1]].forEach(([w, n]) => out.push({ name: 'pop', t: c.W('s6_08', w, n), gain: 0.4 }));
      }
      return out;
    },
    draw(t, part, ctx) {
      const c = VK.cues(ctx), R = Tracks.get('s6'), tr = R.track;
      if (ctx.asking) return s6Ask(c);
      const tau = s6tau(t, part, c), st = R.sim.sample(tau);
      const lines = part.kind === 'lines', k = lines ? s6Times(c) : null;
      const tWh = c.W('s6_03', 'wheels'), e4 = c.E('s6_04'), t6 = c.L('s6_06'), t7 = c.L('s6_07');
      const cam = s6Cam(t, part, c);
      const zoomW = lines ? VK.win(t, tWh - 0.4, e4 + 0.3, 0.6) : 0;
      const tLow = c.W('s6_02', 'lower');
      VK.world(cam, () => {
        VK.ground(cam);
        Coaster.drawTrack(tr, vw, { seed: 3, postEvery: 4, tieEvery: 1.4 });
        if (t > tLow || !lines) { g.save(); g.globalAlpha *= lines ? P(t, tLow, tLow + 0.5) : 1; VK.dashed(-4, 226, 20); g.restore(); }
        Coaster.drawCart(tr, vw, st.s, { h: 50, glow: Math.max(zoomW, Math.min(0.55, st.heat / st.total * 2)) });
      });
      // crest heights, tagged as the cart gets over each one (heights from the track itself)
      const crests = R.marks.crests.concat([R.marks.challenge]);
      const overview = part.kind === 'after' && t > t6 - 0.2;
      crests.forEach((x, i) => {
        const h = tr.at(tr.sAtX(x)).y, [sx, sy] = VK.toScreen(cam, x, h);
        let s;
        if (overview) s = pop(t, c.W('s6_06', i === 0 ? 'tallest' : i < 4 ? 'Every' : 'shorter') + (i > 0 && i < 4 ? (i - 1) * 0.25 : 0));
        else if (lines) {
          const tx = R.sim.timeAtX(x);
          s = i === 0 ? pop(t, k.tR - 0.3) : i === 4 ? pop(t, e4 + 0.9) : tx != null && tx <= k.tauF ? pop(t, k.tR + tx) : 0;
        } else s = 1;
        s *= 1 - zoomW;
        VK.tag(Math.round(h) + ' m', sx, sy - 58, 36, s, { seed: 800 + i, color: i === 0 ? PAL.mustard : i === 4 ? PAL.pink : PAL.cream });
        if (i === 0 && overview) doodleStar(sx + 72, sy - 98, 26, { s: pop(t, c.W('s6_06', 'tallest') + 0.2), seed: 806 });
        if (i === 4 && overview) VK.cross(sx + 84, sy - 60, 0.7, P(t, c.W('s6_06', 'shorter') + 0.3, c.W('s6_06', 'shorter') + 0.7), 807);
      });
      if (t > tLow || !lines) {
        // on the dashed line, over whichever stretch of it is clear of the crest tags
        const clearX = lines && t < e4 + 0.35 ? st.x + 18 : 141, [lx, ly] = VK.toScreen(cam, clearX, 20);
        VK.label('start: 20 m', Math.max(520, lx), ly - 26, 36, lines ? tLow : -1e6, { seed: 810 });
      }
      // the warm wheels close-up
      if (zoomW > 0) {
        const [wx, wy] = VK.toScreen(cam, st.x, st.y);
        hand('warm!', wx + 190, wy + 60, 80, c.L('s6_04'), { color: PAL.tomato, outline: PAL.cream, seed: 812, alpha: zoomW, rot: -0.08 });
      }
      // reply: the stall on the 25 m hill
      if (part.kind === 'reply') {
        const turn = R.sim.turns[0], tStall = tStall0(part);
        if (t > tStall - 0.1) {
          const [sx, sy] = VK.toScreen(cam, tr.x(turn.s), turn.y);
          VK.tag('only ' + R.sim.maxY(k0(part), turn.t + 0.5).toFixed(1) + ' m', sx - 150, sy - 80, 40, pop(t, tStall - 0.1), { color: PAL.red, ink: PAL.cream, seed: 815 });
        }
        VK.answerNote(ctx, part, 1660, 170, 0.3, tStall, { w: 380 });
      }
      // after: friction 1 – coaster 0; every coaster, bike, skateboard
      if (part.kind === 'after') {
        const tFt = c.W('s6_07', 'fight'), sb = pop(t, tFt - 0.2, 0.35) * unpop(t, c.L('s6_08') - 0.2);
        if (sb > 0) {
          g.save(); g.translate(960, 150); g.rotate(-0.02); g.scale(sb, sb);
          paperRect(0, 0, 660, 150, PAL.kraft, { seed: 820, depth: 1.4, tex: 'tex_kraft' });
          g.font = "54px 'Luckiest Guy'"; g.textAlign = 'center'; g.textBaseline = 'middle';
          g.fillStyle = PAL.cream; g.fillText('COASTER 0', -150, 4); g.fillStyle = PAL.red; g.fillText('FRICTION 1', 170, 4);
          g.restore();
        }
        [['coaster', 610], ['bike', 960], ['skateboard', 1310]].forEach(([w, x], i) => {
          const s = pop(t, c.W('s6_08', w), 0.35); if (s <= 0) return;
          g.save(); g.translate(x, 160); g.scale(s, s); doodle(w, 0, 0, 830 + i); g.restore();
          VK.label('every ' + w, x, 250, 40, c.W('s6_08', w) + 0.1, { seed: 840 + i });
        });
      }
      const tTot = c.W('s6_05', 'total'), tTh = c.W('s6_05', 'thermal');
      chartAt(CHART_L, st, { ref: st.total0, max: st.total0 * 1.25, circleTotal: lines ? P(t, tTot, tTot + 0.6) : 0 });
      if (lines && t > tTh) inkCircle(262, 320, 58, { sy: 1.3, w: 5, color: PAL.teal, seed: 850, prog: P(t, tTh, tTh + 0.5) });
      const stg = VK.stage(t, part, [[0, 'inset']]);
      VK.cast(t, ctx, stg, { max: lines && t > c.L('s6_04') - 0.2 && t < c.E('s6_04') + 0.3 ? 'point' : null },
        { avoid: u => s6Action(u, part, c), key: 's6/' + part.kind + (part.answer ?? '') });
      VK.chapter(t, part);
    },
  };
  /* little ink doodles for "every coaster, every bike, every skateboard" (each one warm) */
  function doodle(what, x, y, seed) {
    const w = { w: 5, color: PAL.cream, seed, passes: 2, step: 10 };
    if (what === 'coaster') {
      const pts = []; for (let i = 0; i <= 24; i++) { const u = i / 24; pts.push([x - 110 + u * 220, y + 40 - Math.max(0, Math.sin(u * Math.PI * 1.5)) * 70]); }
      ink(pts, w); paperRect(x - 40, y - 38, 44, 24, PAL.red, { seed: seed + 1, depth: 0.5, rot: -0.6 });
    } else if (what === 'bike') {
      inkCircle(x - 60, y + 20, 36, { ...w }); inkCircle(x + 60, y + 20, 36, { ...w, seed: seed + 1 });
      ink([[x - 60, y + 20], [x - 10, y - 30], [x + 40, y - 30], [x + 60, y + 20]], { ...w, seed: seed + 2 }); ink([[x - 10, y - 30], [x, y + 20], [x - 60, y + 20]], { ...w, seed: seed + 3 });
    } else {
      ink([[x - 100, y + 6], [x - 80, y + 16], [x + 80, y + 16], [x + 100, y + 6]], { ...w, w: 8 });
      paperCircle(x - 60, y + 34, 13, PAL.ink, { seed: seed + 1, depth: 0.3 }); paperCircle(x + 60, y + 34, 13, PAL.ink, { seed: seed + 2, depth: 0.3 });
    }
    for (let j = 0; j < 3; j++) {                // warm: tiny heat squiggles
      const ph = (T * 0.8 + j * 0.33) % 1, sx0 = x - 50 + j * 50, sy0 = y - 40 - ph * 60;
      const pts = []; for (let q = 0; q <= 6; q++) pts.push([sx0 + Math.sin(q * 1.3 + T * 6) * 5, sy0 - q * 6]);
      ink(pts, { w: 3, color: PAL.tomato, alpha: 1 - ph, seed: seed + 20 + j, step: 6, passes: 1 });
    }
  }
  /* PREDICT backdrop: the heat chart (left margin) and the 25 m hill vs the 20 m start line (right margin) */
  function s6Ask(c) {
    const R = Tracks.get('s6'), k = s6Times(c), st = R.sim.sample(k.tauF), tr = R.track;
    const cam = { x: 181, y: 14, z: 0.9, sx: 1730, sy: 520 };
    VK.world(cam, () => {
      VK.ground(cam);
      Coaster.drawTrack(tr, vw, { seed: 3, postEvery: 4, tieEvery: 1.4 });
      VK.dashed(-4, 226, 20);
    }, { clouds: false });
    const [hx, hy] = VK.toScreen(cam, 180, 25);
    VK.tag('25 m', hx, hy - 60, 44, 1, { color: PAL.pink, seed: 861 });
    const [lx, ly] = VK.toScreen(cam, 172, 20);
    VK.label('start: 20 m', lx, ly + 34, 40, -1e6, { dur: 0.01, seed: 860 });
    chartAt([20, 200, 340, 400], st, { ref: st.total0, max: st.total0 * 1.25 });
  }

  /* ======================================================================
     s7 — recap: index cards write on as they are said, the universe's
          receipt, then the BROKEN COASTERS film reel
     ====================================================================== */
  const CARDS = [
    { a: 'PE = m g h', b: '(height stores energy)', x: 700, y: 205 },
    { a: 'KE = ½ m v²', b: '(motion is energy)', x: 1220, y: 205 },
    { a: 'total stays the same', b: '(energy only changes form)', x: 700, y: 490 },
    { a: 'friction → heat', b: '(friction steals it)', x: 1220, y: 490 },
  ];
  function indexCard(cd, i, s, rot, tA, tB, extra) {
    if (s <= 0) return;
    g.save(); g.translate(cd.x, cd.y); g.rotate(rot); g.scale(s, s);
    paperRect(0, 0, 470, 250, PAL.white, { seed: 900 + i, depth: 1.4 });
    g.strokeStyle = 'rgba(90,150,190,0.45)'; g.lineWidth = 2; g.beginPath();
    for (let yy = -40; yy < 120; yy += 42) { g.moveTo(-215, yy); g.lineTo(215, yy); } g.stroke();
    g.strokeStyle = 'rgba(224,83,61,0.5)'; g.beginPath(); g.moveTo(-215, -74); g.lineTo(215, -74); g.stroke();
    hand(cd.a, 0, -22, cd.a.length > 14 ? 52 : 62, tA, { color: PAL.red, seed: 910 + i });
    hand(cd.b, 0, 58, 36, tB, { color: PAL.navy, seed: 920 + i });
    if (extra) extra();
    g.restore();
    tape(cd.x, cd.y - 125 * s, 120 * s, 0.04 * (i % 2 ? 1 : -1), undefined, 930 + i);
  }
  function receipt(x, y, s, t0, total) {
    if (s <= 0) return;
    g.save(); g.translate(x, y); g.rotate(0.05); g.scale(s, s);
    const w = 300, h = 470, pts = [];
    for (let i = 0; i <= 12; i++) pts.push([-w / 2 + i * w / 12, -h / 2 + (i % 2 ? 10 : 0)]);
    for (let i = 12; i >= 0; i--) pts.push([-w / 2 + i * w / 12, h / 2 - (i % 2 ? 12 : 0)]);
    paperPiece(pts, 0, 0, PAL.white, { seed: 950, depth: 1.5 });
    const rows = ['UNIVERSE & CO.', 'RECEIPT', '- - - - - - - - - -', 'borrowed by', 'friction', '', total, 'form: thermal', '- - - - - - - - - -', 'nothing lost.', 'thank you!'];
    g.textAlign = 'center'; g.textBaseline = 'middle';
    rows.forEach((r, i) => {
      if (T < t0 + 0.25 + i * 0.12) return;
      g.fillStyle = r === total ? PAL.red : PAL.ink;
      g.font = r === total ? "40px 'Special Elite'" : i === 1 ? "34px 'Special Elite'" : "26px 'Special Elite'";
      g.fillText(r, 0, -h / 2 + 44 + i * 38);
    });
    g.restore();
  }
  function filmReel(x, y, s, t, seed = 970) {
    if (s <= 0) return;
    if (IMG.reel) { cut('reel', x + 60 * s, y + 20 * s, 360 * s, { rot: Math.sin(t * 1.3) * 0.04, depth: 1.4, seed }); return; }
    g.save(); g.translate(x, y); g.scale(s, s);
    paperPiece([[60, 60], [300, 130], [290, 175], [50, 105]], 0, 0, PAL.ink, { seed: seed + 1, depth: 1 });     // film strip
    for (let i = 0; i < 5; i++) paperRect(95 + i * 44, 104 + i * 13, 28, 22, PAL.sky, { seed: seed + 2 + i, depth: 0, rot: 0.28 });
    g.rotate(t * 0.6);
    paperCircle(0, 0, 130, '#3a332e', { seed, depth: 1.6 });
    for (let k = 0; k < 6; k++) paperCircle(Math.cos(k / 6 * TAU) * 72, Math.sin(k / 6 * TAU) * 72, 28, PAL.paper, { seed: seed + 10 + k, depth: 0 });
    paperCircle(0, 0, 18, PAL.paper, { seed: seed + 20, depth: 0.3 });
    g.restore();
  }
  S.s7 = {
    get timing() { return { lead: { lines: C.lead }, hold: { lines: 2.0 }, gaps: { s7_02: 0.8, s7_04: 0.8 } }; },
    sfx(part, ctx) {
      const c = VK.cues(ctx), out = VK.chapterSfx(part);
      [c.W('s7_01', 'Height'), c.W('s7_01', 'Motion'), c.W('s7_01', 'changes') - 0.4, c.W('s7_02', 'friction')].forEach(t0 => out.push({ name: 'pop', t: t0, gain: 0.4 }, { name: 'scribble', t: t0 + 0.3, gain: 0.3 }));
      out.push({ name: 'scribble', t: c.W('s7_03', 'Borrows'), gain: 0.4 });
      out.push({ name: 'paper', t: c.W('s7_03', 'universe') - 0.2, gain: 0.5 }, { name: 'ding', t: c.W('s7_03', 'receipt'), gain: 0.4 });
      out.push({ name: 'whoosh', t: c.W('s7_05', 'coaster') - 0.2, gain: 0.45 }, { name: 'rolling', t: c.W('s7_05', 'coaster'), gain: 0.25, loop: true, dur: 2 });
      out.push({ name: 'sparkle', t: c.W('s7_05', 'catch'), gain: 0.4 });
      return out;
    },
    draw(t, part, ctx) {
      const c = VK.cues(ctx);
      VK.sky(null);
      const t4 = c.L('s7_04'), mv = E.inOutCubic(P(t, t4 - 0.7, t4 + 0.2));
      const times = [
        [c.W('s7_01', 'Height'), c.W('s7_01', 'm'), c.W('s7_01', 'Height') + 0.4],
        [c.W('s7_01', 'Motion'), c.W('s7_01', 'one'), c.W('s7_01', 'Motion') + 0.4],
        [c.W('s7_01', 'changes') - 0.4, c.W('s7_01', 'total'), c.W('s7_01', 'changes')],
        [c.W('s7_02', 'friction'), c.W('s7_02', 'friction') + 0.2, c.W('s7_02', 'steals')],
      ];
      const tBor = c.W('s7_03', 'Borrows');
      CARDS.forEach((cd, i) => {
        const [tp, tA, tB] = times[i];
        const pos = { x: lerp(cd.x, 450 + i * 340, mv), y: lerp(cd.y, 118, mv), a: cd.a, b: cd.b };
        indexCard(pos, i, pop(t, tp, 0.35) * lerp(1, 0.55, mv), (i % 2 ? 1 : -1) * 0.025, tA, tB, i === 3 ? () => {
          // "Borrows it.": strike "steals", write "borrows"
          if (t > tBor) { VK.strike(-8, 60, 92, 54, P(t, tBor, tBor + 0.3), 940); hand('borrows!', 150, 104, 44, tBor + 0.3, { color: PAL.red, seed: 941, rot: -0.1 }); }
        } : null);
      });
      // the receipt slides in from the right on "the universe keeps the receipt"
      const tU = c.W('s7_03', 'universe') - 0.2, rin = E.outCubic(P(t, tU, tU + 0.6));
      const total = Coaster.fmtJ(Tracks.get('s2').sim.sample(1e9).PE);
      if (rin > 0) receipt(lerp(2150, 1640, rin) + lerp(0, 150, mv), lerp(430, 175, mv), lerp(1, 0.42, mv), tU + 0.3, total);
      // the film reel of broken coasters
      const tC = c.W('s7_05', 'coaster'), rs = pop(t, tC - 0.2, 0.45);
      if (rs > 0) {
        filmReel(1270, 690, rs, t);
        VK.tag('BROKEN COASTERS', 1250, 880, 44, pop(t, tC + 0.25, 0.35), { color: PAL.mustard, seed: 980, rot: -0.04 });
        if (t > c.W('s7_05', 'catch')) UI.stamp('SPOT THE MISTAKE', 820, 560, { s: 0.62 * pop(t, c.W('s7_05', 'catch'), 0.3), rot: -0.08 });
      }
      const stg = VK.stage(t, part, [[0, 'inset'], [t4 - 0.6, 'two']]);
      VK.cast(t, ctx, stg, { voss: t > tC - 0.4 ? 'point' : null, max: t > t4 - 0.2 && t < c.E('s7_04') + 0.3 ? 'point' : null });
      VK.chapter(t, part);
    },
  };

  /* the level: the player itself (Game.complete('video') at the end) */
  class VideoScene extends Player.Scene {}
  Levels.video = VideoScene;
})();
